'use client';

import { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LogoFullWhiteImgSrc from '../../../assets/images/sendit-logo-full-white.svg';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import { getHomeUrl } from '../utils/navigation';
import { Button } from './button';

// Hook to detect if we're on desktop (lg breakpoint and above)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

interface MobileHeaderProps {
  onMenuClick: () => void;
}

export function MobileHeader({ onMenuClick }: MobileHeaderProps) {
  const location = useLocation();
  const { user } = useContext(SessionContext);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    const currentPath = location.pathname + location.search;
    const nextParam = encodeURIComponent(currentPath);
    navigate(`/login?next=${nextParam}`);
  };

  // Determine home URL using centralized logic
  const homeUrl = getHomeUrl(user, location.pathname);

  // Don't render on desktop
  if (isDesktop) {
    return null;
  }

  return (
    <header className="tw:sticky tw:top-0 tw:z-30 tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 tw:bg-brand-black-2 tw:lg:hidden">
      {user && (
        <button
          onClick={onMenuClick}
          className="tw:flex tw:items-center tw:justify-center tw:p-2 tw:text-white tw:hover:bg-zinc-950/10 tw:rounded-lg tw:transition-colors"
          aria-label="Open navigation menu"
        >
          <i className="fas fa-bars tw:text-xl" />
        </button>
      )}
      <Link to={homeUrl} className="tw:flex tw:items-center">
        <img alt="Sendit logo" src={LogoFullWhiteImgSrc.toString()} className="tw:h-8" />
      </Link>
      <div className="tw:w-10" /> {/* Spacer for centering */}
      {!user && (
        <Button small color="brandgreen1" onClick={handleLoginClick}>
          Login
        </Button>
      )}
    </header>
  );
}
